import axios from 'axios';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import { modalStore } from '../modalStore';
import { UpdateLevel, castUpdatesHistory, defaultTitle } from './types';
import { requests } from 'src/requests';
import sentry from 'src/services/sentry';
import { Addons, ISettings, TAddons } from 'src/types/app';
import app from '~/../package.json';
import { LOCALES, updateModalIntervalByLevel } from '~/constants/main';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import versionTimestamp from '~/versionTimestamp.json';

export class AppStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return true;
  }

  @observable public isSoundPlaying: boolean = false;
  @observable public loading: boolean = false;
  @observable public domain: string = '';
  @persist @observable public lastUsage: number | null = Date.now();
  // @persist @observable public cookies: number | null = null;
  @persist @observable public timestamp: number | null = window.SETTINGS ? Date.now() : null;
  @persist('object') @observable public settings?: ISettings = window.SETTINGS;

  checkUpdatesIntervalId: NodeJS.Timeout | null = null;
  @persist @observable lastShowModalUpdate: number = 0;

  @computed get isAddonsAvailable(): Record<TAddons, boolean> {
    return {
      [Addons.PromoCode]: Boolean(this.settings?.is_advanced_promocode),
      [Addons.Deals]: Boolean(this.settings?.is_combo_offer),
      [Addons.Meals]: Boolean(this.settings?.is_combo_meal),
      [Addons.Cashback]: Boolean(this.settings?.is_cashback),
      [Addons.Wallet]: Boolean(this.settings?.is_wallet),
      // On 03/04/2023 parameter "is_mobile_app" was not processed, it was decided to always show.
      [Addons.MobileApp]: Boolean(true),
      [Addons.Webhook]: Boolean(this.settings?.is_webhook),
      [Addons.Kiosk]: Boolean(this.settings?.is_kiosk_app),
      [Addons.Curbside]: Boolean(this.settings?.is_curbside),
      [Addons.Franchise]: Boolean(this.settings?.is_franchise),
      [Addons.PromoBanners]: Boolean(this.settings?.is_promo_banners),
      [Addons.Emenu]: Boolean(this.settings?.is_emenu),
    };
  }

  @computed get formattedCurrency() {
    return localeStore.language === LOCALES.AR ? this.settings?.wallet_currency_ar : this.settings?.wallet_currency;
  }

  @computed get restaurantName(): string {
    if (this.settings?.is_restaurant_name_in_admin_dashboard_title !== true) {
      return defaultTitle;
    }

    const restaurantName = localeStore.language === LOCALES.AR ? this.settings?.restaurant_name_ar : this.settings?.restaurant_name_en;

    return restaurantName ? restaurantName : defaultTitle;
  }

  @action public setRestaurantName = () => {
    document.title = this.restaurantName;
  };

  @action public fetchSettings = async (withLocales: boolean = true) => {
    try {
      this.loading = true;
      const data = await requests.fetchAppSettings();
      this.settings = data;
      this.timestamp = Date.now();
      if (withLocales) {
        await localeStore.fetchLocale();
      }

      // set sentry tags
      sentry.tag('socket', data.socket);
      sentry.tag('release', data.release);
      sentry.tag('restaurant_name', data.restaurant_name_en);

      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'AppStore fetchSettings' });
    }
  };

  @action public changeSoundPlayingState = (playing: boolean) => {
    this.isSoundPlaying = playing;
  };

  afterHydration() {
    if (!this.settings || process.env.NODE_ENV === 'development') {
      this.fetchSettings();
    } else if (window.SETTINGS) {
      this.settings = window.SETTINGS;
      localeStore.fetchLocale();

      // set sentry tags
      sentry.tag('socket', this.settings.socket);
      sentry.tag('release', this.settings.release);
      sentry.tag('restaurant_name', this.settings.restaurant_name_en);
    } else {
      localeStore.fetchLocale();
    }
    this.timestamp = Date.now();
  }

  @action checkUpdates = flow(function* (this: AppStore) {
    try {
      const checkUpdatesURL =
        process.env.NODE_ENV === 'development'
          ? `/static/updates-history.json`
          : `https://${process.env.REACT_APP_REACT_ADMIN_DOMAIN || ''}/static/updates-history.json`;

      const { data } = yield axios.get(checkUpdatesURL, { headers: { Accept: '*/*' }, responseType: 'json' });

      const { version, updates } = castUpdatesHistory(data);
      const newUpdates = updates.filter((update) => update.timestamp > versionTimestamp.timestamp).sort((a, b) => a.timestamp - b.timestamp);

      let updateModalInterval = 0;

      // If server has different version set update as HIGH level
      if (/^[\d.]+\d$/.test(version) && version !== app.version) {
        updateModalInterval = updateModalIntervalByLevel[UpdateLevel.High];
      } else if (newUpdates.length > 0) {
        // Get smallest show modal interval (highest level)
        updateModalInterval = newUpdates.map((newFix) => updateModalIntervalByLevel[newFix.level]).sort()[0];
      }

      const lastShowModalTimeAgo = +new Date() - this.lastShowModalUpdate;

      if (updateModalInterval > 0 && lastShowModalTimeAgo > updateModalInterval) {
        this.lastShowModalUpdate = +new Date();
        modalStore.openUpdateModal();
      } else if (updateModalInterval === 0) {
        this.lastShowModalUpdate = 0;
      }
    } catch (error) {
      logger.error({ error, place: 'AppStore checkUpdates' });
    }
  }).bind(this);

  @action stopCheckUpdates = () => {
    if (this.checkUpdatesIntervalId) {
      clearInterval(this.checkUpdatesIntervalId);
    }
  };

  @action runCheckUpdates = () => {
    this.checkUpdates();
    this.checkUpdatesIntervalId = setInterval(() => {
      this.checkUpdates();
    }, 60 * 1000);
  };

  @action runUpdate = () => {
    this.lastShowModalUpdate = 0;
    window.location.reload();
  };

  @computed get needUpdate(): boolean {
    return !!this.lastShowModalUpdate;
  }
}

const appStore = new AppStore();

export { appStore };
